import { useAppSelector } from '@/store/StoreHooks';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Loader from '../common/Loader/Loader';

interface AuthGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean;
}

function AuthGuard({ children, requireAuth = true }: AuthGuardProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const enableMembersLoginSignup = useAppSelector(
    (val) => val.selectedRestaurant.restaurant.enable_members_login_signup
  );
  const restaurantLoading = useAppSelector(
    (val) => val.selectedRestaurant.restaurantLoading
  );
  const userDetail = useAppSelector((val) => val.userDetail);

  useEffect(() => {
    if (restaurantLoading) return;
    const checkAuth = () => {
      const token = localStorage.getItem('token');

      if (requireAuth) {
        if (enableMembersLoginSignup === false) {
          localStorage.removeItem('token');
          navigate('/', { replace: true });
          return;
        }
        if (!token) {
          // Not authenticated, redirect to login
          navigate('/login', { replace: true });
          return;
        }

        // Check if token is valid (you can add more validation here)
        try {
          setIsAuthenticated(true);
          if (userDetail.id && userDetail.member_payment_methods.length === 0) {
            navigate('/profile/payment-method', { replace: true });
          }
        } catch (error) {
          localStorage.removeItem('token');
          navigate('/login', { replace: true });
          return;
        }
      } else {
        // For public routes, check if user is already logged in
        if (token) {
          if (['/login', '/signup'].includes(window.location.pathname)) {
            navigate('/', { replace: true });
            return;
          }
          try {
            setIsAuthenticated(true);
          } catch (error) {
            // Clear invalid data
            localStorage.removeItem('token');
            localStorage.removeItem('user');
          }
        } else {
          if (
            !enableMembersLoginSignup &&
            ['/login', '/signup'].includes(window.location.pathname)
          ) {
            navigate('/', { replace: true });
            return;
          }
        }
      }

      setIsLoading(false);
    };

    checkAuth();
  }, [
    navigate,
    requireAuth,
    enableMembersLoginSignup,
    userDetail.id,
    userDetail.member_payment_methods,
    restaurantLoading,
    searchParams,
  ]);

  // Show loader while checking authentication or if restaurant info is loading
  if (isLoading || restaurantLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          background: 'linear-gradient(135deg, #2c1810 0%, #1a0f0a 100%)',
        }}
      >
        <Loader load={true} />
      </div>
    );
  }

  // If authentication is required and user is not authenticated, don't render children
  if (requireAuth && !isAuthenticated) {
    return null;
  }

  return <>{children}</>;
}

export default AuthGuard;
